import React, { useRef, useEffect, useState } from 'react';
import { API_BASE } from '../config';

export default function VideoPlayer({ frame, fps, progress, videoId, videos, onRefresh }) {
  const canvasRef = useRef(null);
  const fpsRef    = useRef([]);
  const [displayFps, setDisplayFps] = useState(0);
  const [pollCount, setPollCount]   = useState(0);

  // Draw incoming WS frames onto canvas while processing
  useEffect(() => {
    if (!frame || !canvasRef.current) return;
    const now   = performance.now();
    const times = fpsRef.current;
    times.push(now);
    while (times.length > 30) times.shift();
    if (times.length > 1)
      setDisplayFps(1000 * (times.length - 1) / (times[times.length - 1] - times[0]));

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const img    = new Image();
    img.onload = () => {
      if (canvas.width  !== img.width)  canvas.width  = img.width;
      if (canvas.height !== img.height) canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
    };
    img.src = `data:image/jpeg;base64,${frame}`;
  }, [frame]);

  const currentVideo = videos?.find(v => v.id === videoId);
  const isComplete   = currentVideo?.status === 'complete';
  const annotatedUrl = currentVideo?.annotated_url
    ? `${API_BASE}${currentVideo.annotated_url}`
    : null;

  // After completion, poll every 2 s until annotated_url appears (max 20 tries ≈ 40 s)
  useEffect(() => {
    if (!isComplete || annotatedUrl || !onRefresh || pollCount >= 20) return;
    const id = setTimeout(() => {
      onRefresh();
      setPollCount(n => n + 1);
    }, 2000);
    return () => clearTimeout(id);
  }, [isComplete, annotatedUrl, onRefresh, pollCount]);

  // Reset poll counter whenever the selected video changes
  useEffect(() => {
    setPollCount(0);
  }, [videoId]);

  const showAnnotated  = isComplete && annotatedUrl && !progress;
  const showCanvas     = frame && !showAnnotated;
  const waitAnnotated  = isComplete && !annotatedUrl && !progress && pollCount < 20;
  const failAnnotated  = isComplete && !annotatedUrl && !progress && pollCount >= 20;

  return (
    <div className="h-full flex flex-col bg-black/30 overflow-hidden">
      {/* relative flex-1 is sized by the flex layout; content is absolutely positioned
          inside so it can never push or shrink the container. */}
      <div className="relative flex-1 bg-black/50 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">

          {showAnnotated && (
            <video
              key={annotatedUrl}
              src={annotatedUrl}
              controls
              autoPlay
              muted
              loop
              className="w-full h-full"
              style={{ objectFit: 'contain' }}
            />
          )}

          {showCanvas && (
            <>
              <canvas ref={canvasRef} className="max-w-full max-h-full" />
              <div className="absolute top-3 right-3 bg-black/60 text-green-400 text-xs
                              px-2 py-1 rounded font-mono">
                {displayFps.toFixed(1)} FPS
              </div>
              {videoId && (
                <div className="absolute top-3 left-3 bg-black/60 text-slate-300 text-xs
                                px-2 py-1 rounded font-mono">
                  Video #{videoId}
                </div>
              )}
            </>
          )}

          {waitAnnotated && (
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Preparing annotated video…</span>
            </div>
          )}

          {failAnnotated && (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <span className="text-3xl">⚠️</span>
              <span className="text-sm">Annotated video unavailable</span>
              <button onClick={onRefresh}
                className="text-xs text-brand border border-brand/40 px-3 py-1 rounded hover:bg-brand/10 transition-colors">
                Retry
              </button>
            </div>
          )}

          {!showAnnotated && !showCanvas && !waitAnnotated && !failAnnotated && (
            <div className="flex flex-col items-center gap-2 text-slate-600">
              <span className="text-4xl">🎬</span>
              <span className="text-sm">
                {progress ? 'Processing…' : 'Upload a video or start the webcam'}
              </span>
              {!progress && <span className="text-xs">No stream active</span>}
            </div>
          )}

        </div>
      </div>

      {/* Progress bar */}
      {progress && (
        <div className="px-4 py-2 bg-surface-card border-t border-surface-border">
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Processing frames…</span>
            <span>{progress.frames_processed} / {progress.total_frames} ({progress.percent?.toFixed(1)}%)</span>
          </div>
          <div className="h-1 bg-surface-border rounded-full overflow-hidden">
            <div className="h-full bg-brand transition-all rounded-full"
                 style={{ width: `${progress.percent || 0}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}
